import { useCallback, useEffect, useState } from 'react';
import {
  deleteSalespersonEmail,
  fetchSalespersonEmails,
  renameSalespersonCode,
  upsertSalespersonEmail,
  type SalespersonEmailRow,
} from '../../../lib/applicationFormSettings';
import { useFormsSettingsRealtime } from '../../../hooks/useFormsSettingsRealtime';
import { Modal } from '../../ui/Modal';
import { publicAccountCreationUrl } from '../../../types/applicationForm';

type SalespersonPanelProps = {
  onChanged?: () => void;
  /** Kept for call-site compatibility; panel is only used from User management. */
  embedded?: boolean;
};

/** Salesperson master list — managed only from User management. */
export function ApplicationFormSalespersonPanel({ onChanged }: SalespersonPanelProps) {
  const [rows, setRows] = useState<SalespersonEmailRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [draftCode, setDraftCode] = useState('');
  const [editCode, setEditCode] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState('');
  const [copiedLinkCode, setCopiedLinkCode] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await fetchSalespersonEmails();
      setRows(
        list
          .filter((row) => row.active)
          .sort((a, b) => a.sort_order - b.sort_order || a.code.localeCompare(b.code)),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load salesperson codes.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useFormsSettingsRealtime({
    tables: ['salesperson_emails'],
    onChange: () => {
      void load();
    },
  });

  function openCreate() {
    setError(null);
    setMessage(null);
    setDraftCode('');
    setCreateOpen(true);
  }

  function closeCreate() {
    if (saving) return;
    setCreateOpen(false);
    setDraftCode('');
  }

  function openEdit(code: string) {
    setError(null);
    setMessage(null);
    setEditCode(code);
    setEditDraft(code);
  }

  function closeEdit() {
    if (saving) return;
    setEditCode(null);
    setEditDraft('');
  }

  async function copyApplyLink(code: string) {
    const url = publicAccountCreationUrl(code);
    setError(null);
    try {
      await navigator.clipboard.writeText(url);
      setCopiedLinkCode(code);
      setMessage(`Copied apply link for ${code}.`);
      window.setTimeout(() => setCopiedLinkCode((prev) => (prev === code ? null : prev)), 2000);
    } catch {
      window.prompt('Copy apply link:', url);
    }
  }

  async function addRow(e: React.FormEvent) {
    e.preventDefault();
    const code = draftCode.trim();
    if (!code) {
      setError('Salesperson code is required.');
      return;
    }
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      await upsertSalespersonEmail({
        code,
        sort_order: (rows.length + 1) * 10,
      });
      setCreateOpen(false);
      setDraftCode('');
      setMessage('Salesperson code added.');
      await load();
      onChanged?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add salesperson code.');
    } finally {
      setSaving(false);
    }
  }

  async function submitEdit(e: React.FormEvent) {
    e.preventDefault();
    if (!editCode) return;
    const trimmed = editDraft.trim();
    if (!trimmed) {
      setError('Salesperson code is required.');
      return;
    }
    if (trimmed === editCode) {
      closeEdit();
      return;
    }
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      await renameSalespersonCode(editCode, trimmed);
      setEditCode(null);
      setEditDraft('');
      setMessage(`Renamed to ${trimmed}.`);
      await load();
      onChanged?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to rename salesperson code.');
    } finally {
      setSaving(false);
    }
  }

  async function removeCode(code: string) {
    if (!window.confirm(`Delete salesperson code "${code}"?`)) return;
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      await deleteSalespersonEmail(code);
      setMessage(`Deleted ${code}.`);
      await load();
      onChanged?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete salesperson code.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <div className="unified-users-create">
        <div className="unified-users-panel__title-row">
          <p className="settings-panel__lead unified-users-dept-modal__lead">
            Manage salesperson codes here, then assign them to each user. Renaming a code also updates
            accounts that use it, so visibility remains in sync. Assigned users receive Application Form
            sales notifications for that code. Use Copy link for each code&apos;s customer apply URL.
            Default sales email (fallback) is under Application Form → Email.
          </p>
          {!createOpen ? (
            <button type="button" className="btn-primary btn-sm" onClick={openCreate}>
              Add code
            </button>
          ) : null}
        </div>

        {createOpen ? (
          <form className="settings-add-card unified-users-dept-create" onSubmit={(e) => void addRow(e)}>
            <h3 className="settings-add-card__title">Add salesperson code</h3>
            <label className="field">
              <span className="field-label">Code</span>
              <input
                className="field-input"
                value={draftCode}
                onChange={(e) => setDraftCode(e.target.value)}
                placeholder="SA1 - MK"
                required
                autoFocus
              />
            </label>
            <div className="unified-users-create__footer" style={{ marginTop: 8 }}>
              <button type="button" className="btn-secondary btn-sm" onClick={closeCreate} disabled={saving}>
                Cancel
              </button>
              <button type="submit" className="btn-primary btn-sm" disabled={saving}>
                {saving ? 'Saving…' : 'Add'}
              </button>
            </div>
          </form>
        ) : null}

        {error && !editCode ? <p className="alert alert-error">{error}</p> : null}
        {message ? <p className="alert alert-success">{message}</p> : null}

        {loading ? (
          <p className="page-lead">Loading salesperson codes…</p>
        ) : rows.length === 0 ? (
          <p className="settings-empty">No salesperson codes have been added yet.</p>
        ) : (
          <div className="settings-table-wrap">
            <table className="settings-table">
              <thead>
                <tr>
                  <th>Code</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.code}>
                    <td>
                      <code>{row.code}</code>
                    </td>
                    <td>
                      <div className="af-settings-table__actions" style={{ display: 'flex', gap: 6 }}>
                        <button
                          type="button"
                          className="btn-secondary btn-sm"
                          disabled={saving}
                          onClick={() => void copyApplyLink(row.code)}
                        >
                          {copiedLinkCode === row.code ? 'Copied' : 'Copy link'}
                        </button>
                        <button
                          type="button"
                          className="btn-secondary btn-sm"
                          disabled={saving}
                          onClick={() => openEdit(row.code)}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="btn-secondary btn-sm"
                          disabled={saving}
                          onClick={() => void removeCode(row.code)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal title="Edit salesperson code" open={editCode != null} onClose={closeEdit}>
        <form className="unified-users-create" onSubmit={(e) => void submitEdit(e)}>
          {error && editCode ? <p className="alert alert-error">{error}</p> : null}
          <label className="field">
            <span className="field-label">Code</span>
            <input
              className="field-input"
              value={editDraft}
              onChange={(e) => setEditDraft(e.target.value)}
              required
              autoFocus
              disabled={saving}
            />
          </label>
          <div className="unified-users-create__footer">
            <button type="button" className="btn-secondary btn-sm" onClick={closeEdit} disabled={saving}>
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary btn-sm"
              disabled={saving || !editDraft.trim() || editDraft.trim() === editCode}
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </Modal>
    </>
  );
}
